import { useQuinceRouter } from '@src/modules/navigation'
import {
  Filter,
  FilterKeys,
  EFilterKeys,
  SelectedFilter,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { PRODUCT_FILTER_KEYS } from '@src/quince-site-common/src/constants/product'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { FilterOption, CheckboxOption, FilterItem, UpdateQueryParams } from './filterSection.interface'

// Constants
const FILTER_UPDATE_TYPES = {
  TOGGLE: 'toggle',
  OVERWRITE: 'overwrite',
} as const

/**
 * Maps filter labels to their corresponding filter keys
 */
// getFilterKeyFromLabel function removed

const DEPARTMENT_EXCLUDE_SLUG_LIST = [
  '/men',
  '/women',
  '/women/new-arrivals',
  '/men/new-arrivals',
  '/women/best-sellers',
  '/men/best-sellers',
  '/all/new-arrivals',
]

const JUMPLINK_PARAM = 'jumplink=true'

interface RouterContext {
  router: ReturnType<typeof useQuinceRouter>
  routerAsPath: string
}

interface RadioFilterChangeParams {
  value: string
  options: CheckboxOption[]
  filter: FilterItem
  showGroupedPage: boolean
  updateQuery: (params: UpdateQueryParams) => void
  serverFilters: Filter[]
  subCollectionCards?: SubCollectionCard[]
}

export function excludeDepartmentFilter(filters: Filter[], slug: string | undefined) {
  if (!slug) return filters
  return DEPARTMENT_EXCLUDE_SLUG_LIST.includes(slug)
    ? filters.filter(filter => filter.name !== (PRODUCT_FILTER_KEYS.departments as string))
    : filters
}

/**
 * Converts a string to title case
 */
export function toTitleCase(str: string) {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Creates checkbox options from filter values
 */
export function createCheckboxOptions(
  values: (string | FilterOption)[],
  selectedValues: string[],
  filterName: string,
): CheckboxOption[] {
  return values.map((value, index) => {
    const name = typeof value === 'string' ? value : value.name
    const displayName = typeof value === 'string' ? value : (value.displayName ?? value.name)
    return {
      id: `${filterName}-${displayName}-${index}`, // Include filterName in ID
      label: displayName,
      name: name,
      checked: selectedValues.includes(name),
    }
  })
}

/**
 * Handles navigation to a URL
 */
export function navigateTo(url: string, isJumpLink = false, router: ReturnType<typeof useQuinceRouter>) {
  if (!url) return

  // Handle absolute URLs
  if (url.startsWith('http://') || url.startsWith('https://')) {
    window.location.href = url
    return
  }

  // Use a simple path format
  const formattedUrl = url.startsWith('/') ? url : `/${url}`
  // @TODO: This logic will be removed in the future when productType is multiSelect.
  if (isJumpLink) {
    router.push(`${formattedUrl}?jumplink=true`)
    return
  } else {
    router.push(formattedUrl)
    return
  }
}

/**
 * Calculates the total number of selected filters across all filter types
 */
export const calculateTotalSelectedFilters = (selectedFilters: any): number => {
  return Object.values(selectedFilters).reduce((total: number, filterValues) => {
    // Handle undefined, null, or non-array values
    if (!filterValues) return total
    if (!Array.isArray(filterValues)) return total
    return total + filterValues.length
  }, 0)
}

/**
 * Checks if a filter has any selected values
 * @param filter The filter item to check
 * @param selectedFiltersState Map of filter states
 * @returns Boolean indicating if the filter has selected values
 */
export const hasSelectedValues = (filter: FilterItem, selectedFiltersState: Record<string, string[]>) => {
  const state = selectedFiltersState[filter.name]
  return state ? state.length > 0 : false
}

/**
 * Gets the count of selected values for a filter
 * @param filter The filter item to check
 * @param selectedFiltersState Map of filter states
 * @param showGroupedPage Whether this is a grouped page (optional)
 * @returns Number of selected values
 */
export const getSelectedCount = (
  filter: FilterItem,
  selectedFiltersState: Record<string, string[]>,
  showGroupedPage?: boolean,
) => {
  // For Product Type filters on grouped pages, the state is stored under SUBCOLLECTIONS
  let filterKey = filter.name
  if (showGroupedPage && filterKey === EFilterKeys.PRODUCT_TYPE) {
    filterKey = EFilterKeys.SUBCOLLECTIONS
  }

  const state = selectedFiltersState[filterKey]
  return state ? state.length : 0
}

export const getDisplayNameForFilterValue = ({
  filters,
  filterName,
  filterValue,
}: {
  filters: Filter[]
  filterName: string
  filterValue: FilterKeys
}): string => {
  const filter = filters.find(f => f.name === filterName)
  if (!filter) return filterValue

  const filterValueOption = filter.values.find(value => {
    if (typeof value === 'string') {
      return value === filterValue
    }
    // As filterValueOption value will be in uppercase need to check value.name as well.
    return value.value === filterValue || value.name === filterValue
  })

  if (typeof filterValueOption === 'string') {
    return filterValueOption
  }

  return filterValueOption?.displayName ?? filterValueOption?.name ?? filterValue
}

/**
 * Normalizes text to kebab-case format for consistent comparison
 * @param text The text to normalize
 * @returns Normalized kebab-case string
 */
export function normalizeToKebabCase(text: string): string {
  return (
    text
      .toLowerCase()
      // Replace any non-alphanumeric characters with hyphens
      .replace(/[^a-z0-9]+/g, '-')
      // Remove all hyphens at start and end
      .replace(/^-+/, '')
      .replace(/-+$/, '')
  )
}

/**
 * Finds a matching filter option based on URL segment
 * @param options Array of filter options to search through
 * @param urlSegment The URL segment to match against
 * @returns Matching option or undefined
 */
export function findMatchingFilterOption(options: CheckboxOption[], urlSegment: string): CheckboxOption | undefined {
  const normalizedUrlSegment = normalizeToKebabCase(urlSegment)

  return options.find(opt => {
    const normalizedLabel = normalizeToKebabCase(opt.label)
    const normalizedId = normalizeToKebabCase(opt.id)

    return normalizedLabel === normalizedUrlSegment || normalizedId === normalizedUrlSegment
  })
}

/**
 * Detects if there's a URL-based product type selection for grouped pages
 * @param routerAsPath The current router asPath
 * @param collectionSlug The collection slug
 * @param serverFilters Array of server filters
 * @param selectedFiltersState Current selected filters state
 * @returns Object with matching option and whether there's a URL-based selection
 */
export function detectUrlBasedProductTypeSelection(
  routerAsPath: string,
  collectionSlug: string | undefined,
  serverFilters: Filter[],
  selectedFiltersState: Record<string, string[]>,
): { matchingOption: string | FilterOption | undefined; hasUrlBasedSelection: boolean } {
  const formattedPathname = routerAsPath.startsWith('/') ? routerAsPath.slice(1) : routerAsPath
  const pathSegments = formattedPathname.split('/')
  const lastSegment = pathSegments[pathSegments.length - 1]

  if (!lastSegment || !collectionSlug) {
    return { matchingOption: undefined, hasUrlBasedSelection: false }
  }

  // Check if the lastSegment corresponds to a product type filter option
  const productTypeFilter = serverFilters.find(filter => filter.name === EFilterKeys.PRODUCT_TYPE)
  if (!productTypeFilter) {
    return { matchingOption: undefined, hasUrlBasedSelection: false }
  }

  const normalizedLastSegment = normalizeToKebabCase(lastSegment)

  const matchingOption = productTypeFilter.values.find(value => {
    const valueName = typeof value === 'string' ? value : value.name
    const normalizedValueName = normalizeToKebabCase(valueName)
    return normalizedValueName === normalizedLastSegment
  })

  const hasUrlBasedSelection =
    !!matchingOption && !selectedFiltersState[EFilterKeys.SUBCOLLECTIONS]?.includes(lastSegment)

  return { matchingOption, hasUrlBasedSelection }
}

/**
 * Gets the total selected filters count including URL-based selection for grouped pages
 * @param selectedFiltersState Current selected filters state
 * @param showGroupedPage Whether this is a grouped page
 * @param routerAsPath Current router path
 * @param serverFilters Available filters from server
 * @returns Total count of selected filters
 */
export function getTotalSelectedFiltersCount(
  selectedFiltersState: Record<string, string[]>,
  showGroupedPage: boolean,
  routerAsPath: string,
  serverFilters: Filter[],
): number {
  let totalCount = Object.values(selectedFiltersState).reduce((total: number, filterValues) => {
    if (!filterValues) return total
    if (!Array.isArray(filterValues)) return total
    return total + filterValues.length
  }, 0)

  // For grouped pages, check if there's a URL-based selection that isn't already counted
  if (showGroupedPage) {
    const formattedPathname = routerAsPath.startsWith('/') ? routerAsPath.slice(1) : routerAsPath
    const pathSegments = formattedPathname.split('/')
    const lastSegment = pathSegments[pathSegments.length - 1]

    if (lastSegment) {
      const productTypeFilter = serverFilters.find(filter => filter.name === String(EFilterKeys.PRODUCT_TYPE))
      if (productTypeFilter) {
        const matchingValue = productTypeFilter.values.find(value => {
          const valueName = typeof value === 'string' ? value : value.name
          const normalizedValueName = valueName
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            // Remove all hyphens at start and end
            .replace(/^-+/, '')
            .replace(/-+$/, '')
          const normalizedLastSegment = lastSegment
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            // Remove all hyphens at start and end
            .replace(/^-+/, '')
            .replace(/-+$/, '')
          return normalizedValueName === normalizedLastSegment
        })

        // Only count if it's not already in selectedFiltersState
        if (matchingValue && !selectedFiltersState[String(EFilterKeys.SUBCOLLECTIONS)]?.includes(lastSegment)) {
          totalCount += 1
        }
      }
    }
  }

  return totalCount
}

/**
 * Gets the currently selected radio option based on URL for grouped pages
 * @param options Available radio options
 * @param routerAsPath Current router path
 * @returns Selected option if found, undefined otherwise
 */
export function getUrlBasedSelectedOption(options: CheckboxOption[], routerAsPath: string): CheckboxOption | undefined {
  const formattedPathname = routerAsPath.startsWith('/') ? routerAsPath.slice(1) : routerAsPath
  const pathSegments = formattedPathname.split('/')
  const lastSegment = pathSegments[pathSegments.length - 1]

  if (!lastSegment) return undefined

  return findMatchingFilterOption(options, lastSegment)
}

interface SubCollectionCard {
  id: string
  title: string
  slug: string
}

/**
 * Gets the title from the current path by matching against subcollection cards
 */
export function getTitleFromPath(currentPath: string, subCollectionCards?: SubCollectionCard[]): string {
  if (!subCollectionCards?.length) return ''

  const matchingCard = subCollectionCards.find(card => card.slug === currentPath)
  return matchingCard?.title ?? ''
}

/**
 * Updates filter state based on URL for product type in grouped pages
 */
export function handleUrlBasedProductTypeSelection(
  filter: FilterItem,
  options: CheckboxOption[],
  routerAsPath: string,
  selectedFiltersState: Record<string, string[]>,
  subCollectionCards?: SubCollectionCard[],
): { currentValue: string; hasUrlMatch: boolean } {
  const filterName = filter.name === EFilterKeys.PRODUCT_TYPE ? EFilterKeys.SUBCOLLECTIONS : filter.name
  let currentValue = selectedFiltersState[filterName]?.[0] ?? ''
  let hasUrlMatch = false

  // Get title from current path
  const pathTitle = getTitleFromPath(routerAsPath, subCollectionCards)
  if (pathTitle) {
    const matchingOption = options.find(opt => (opt.name || opt.label) === pathTitle)
    if (matchingOption) {
      currentValue = matchingOption.name || matchingOption.label
      hasUrlMatch = true
      // Update the checked property of the matching option
      matchingOption.checked = true
      // Update selectedFiltersState to reflect the selection
      selectedFiltersState[filterName] = [matchingOption.name || matchingOption.label]
    }
  }

  return { currentValue, hasUrlMatch }
}

export function handleRadioFilterChange(routerContext: RouterContext, params: RadioFilterChangeParams): void {
  const { router, routerAsPath } = routerContext
  const { value, options, filter, showGroupedPage, updateQuery, serverFilters, subCollectionCards } = params

  const selectedOption = options.find(opt => opt.id === value)

  if (selectedOption) {
    const filterName = filter.name === EFilterKeys.SUBCOLLECTIONS ? EFilterKeys.PRODUCT_TYPE : filter.name
    trackEvent(TELEMETRY_EVENT_NAMES.FILTER_APPLIED, {
      filter_key: filterName,
      filter_value: selectedOption.label,
    })

    if (filter.name === EFilterKeys.PRODUCT_TYPE && showGroupedPage) {
      const currentTitle = getTitleFromPath(routerAsPath, subCollectionCards)

      if (currentTitle === (selectedOption.name || selectedOption.label)) {
        handleProductTypeRouting(router, null, routerAsPath, serverFilters, true, subCollectionCards)
      } else {
        handleProductTypeRouting(
          router,
          selectedOption.name || selectedOption.label,
          routerAsPath,
          serverFilters,
          true,
          subCollectionCards,
        )
      }
    } else if (filterName === EFilterKeys.PRODUCT_TYPE && !showGroupedPage) {
      const newSelectedFilters = SelectedFilter.initializeWithProductType(selectedOption.name || selectedOption.label)
      updateQuery({
        filter: {
          type: FILTER_UPDATE_TYPES.OVERWRITE,
          updatedFilters: newSelectedFilters,
        },
      })
    } else {
      updateQuery({
        filter: {
          type: FILTER_UPDATE_TYPES.TOGGLE,
          name: filter.name,
          value: selectedOption.name || selectedOption.label,
          label: filter.config.label,
        },
      })
    }
  }
}

/**
 * Handles routing for product type filter changes
 * @param router Router instance
 * @param selectedValue Selected filter value (or null/undefined to clear)
 * @param currentPath Current router path
 * @param serverFilters Available filters from server
 * @param addJumplink Whether to add jumplink parameter
 */
export function handleProductTypeRouting(
  router: ReturnType<typeof useQuinceRouter>,
  selectedValue: string | null | undefined,
  currentPath: string,
  serverFilters: Filter[],
  addJumplink = true,
  subCollectionCards?: SubCollectionCard[],
): void {
  if (!selectedValue) {
    // When clearing, just remove the last segment of the current path
    const pathSegments = currentPath.split('/')
    pathSegments.pop()
    let newPath = pathSegments.join('/')

    if (addJumplink) {
      newPath += `?${JUMPLINK_PARAM}`
    }

    if (currentPath !== newPath) {
      router.push(newPath)
    }
    return
  }

  // Find the matching subcollection card to get its slug
  const matchingCard = subCollectionCards?.find(card => card.title === selectedValue)
  if (matchingCard) {
    let newPath = matchingCard.slug
    if (addJumplink) {
      newPath += `?${JUMPLINK_PARAM}`
    }
    if (currentPath !== newPath) {
      router.push(newPath)
    }
    return
  }
}

/**
 * Enhanced chip generation that includes URL-based selections for grouped pages
 * @param baseSelectedFilterChips Chips from useFilterState hook
 * @param showGroupedPage Whether this is a grouped page
 * @param routerAsPath Current router path
 * @param subCollectionCards Array of subcollection cards
 * @returns Enhanced chips array including URL-based selections
 */
export function getEnhancedSelectedFilterChips(
  baseSelectedFilterChips: { label: string; filterType: string; value: string }[],
  showGroupedPage: boolean,
  routerAsPath: string,
  subCollectionCards?: SubCollectionCard[],
): { label: string; filterType: string; value: string }[] {
  const chips = [...baseSelectedFilterChips]

  // For grouped pages, add URL-based subcollection chip if present
  if (showGroupedPage) {
    const currentTitle = getTitleFromPath(routerAsPath, subCollectionCards)
    if (currentTitle) {
      // Check if this chip already exists
      const chipExists = chips.some(
        chip =>
          chip.label === currentTitle &&
          (chip.filterType === EFilterKeys.SUBCOLLECTIONS || chip.filterType === EFilterKeys.PRODUCT_TYPE),
      )

      if (!chipExists) {
        chips.push({
          label: currentTitle,
          filterType: EFilterKeys.SUBCOLLECTIONS,
          value: currentTitle,
        })
      }
    }
  }

  return chips
}

/**
 * Enhanced clear all function that handles URL-based selections for grouped pages
 */
export function getEnhancedClearAllFilters(
  baseClearAllFilters: () => void,
  showGroupedPage: boolean,
  routerAsPath: string,
  router: ReturnType<typeof useQuinceRouter>,
  subCollectionCards?: SubCollectionCard[],
): () => void {
  return () => {
    // For grouped pages, check if there's a URL-based selection first
    if (showGroupedPage) {
      // Get the current title from path
      const currentTitle = getTitleFromPath(routerAsPath, subCollectionCards)
      if (currentTitle) {
        // If we're on a product type page, navigate back to parent
        const pathSegments = routerAsPath.split('/')
        pathSegments.pop() // Remove the last segment
        const parentPath = pathSegments.join('/')
        router.push(`${parentPath}?${JUMPLINK_PARAM}`)
        return // Let the navigation handle state reset
      }
    }

    // If no URL-based selection or not a grouped page, just clear regular filters
    baseClearAllFilters()
  }
}

/**
 * Enhanced chip dismiss function that handles URL-based selections for grouped pages
 */
export function getEnhancedHandleChipDismissByLabel(
  baseHandleChipDismissByLabel: (value: string) => void,
  showGroupedPage: boolean,
  routerAsPath: string,
  serverFilters: Filter[],
  router: ReturnType<typeof useQuinceRouter>,
): (value: string) => void {
  return (value: string) => {
    // For grouped pages, check if this is a product type chip
    if (showGroupedPage) {
      const productTypeFilter = serverFilters.find(filter => filter.name === EFilterKeys.PRODUCT_TYPE)
      if (productTypeFilter) {
        const matchingValue = productTypeFilter.values.find(filterValue => {
          const valueName = typeof filterValue === 'string' ? filterValue : filterValue.name
          return valueName === value
        })

        if (matchingValue) {
          // This is a product type chip, handle URL-based navigation
          handleProductTypeRouting(router, null, routerAsPath, serverFilters)
          return
        }
      }
    }

    // Handle regular chips
    baseHandleChipDismissByLabel(value)
  }
}

/**
 * Formats a slug to be URL-friendly
 */
export function formatProductTypeSlug(slug: string): string {
  return slug
    .toLowerCase()
    .replace(/[&]/g, '') // Remove &
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^a-z0-9-]/g, '') // Remove any other special characters
    .replace(/^-+/, '') // Remove hyphens at start
    .replace(/-+$/, '') // Remove hyphens at end
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
}

/**
 * Transforms raw subcollection cards into a standardized format
 * @param cards Raw subcollection cards from props
 * @returns Transformed subcollection cards with standardized types
 */
export function transformSubCollectionCards(cards: any[] | undefined): SubCollectionCard[] | undefined {
  return cards?.map(card => ({
    id: String(card.id),
    title: card.title || '',
    slug: card.slug || '',
  }))
}

/**
 * Prepares radio filter data with common logic for both sidebar and mobile components
 */
export function prepareRadioFilterData(
  filter: FilterItem,
  selectedFiltersState: Record<string, string[]>,
  showGroupedPage: boolean,
  routerAsPath: string,
  subCollectionCards?: SubCollectionCard[],
): {
  filterName: string
  state: string[]
  currentValue: string
  options: CheckboxOption[]
} {
  const filterName = filter.name === EFilterKeys.PRODUCT_TYPE ? EFilterKeys.SUBCOLLECTIONS : filter.name
  const state = selectedFiltersState[filterName] ?? []
  let currentValue = state[0] || ''
  const options = createCheckboxOptions(filter.values, state, filterName)

  // For grouped pages, use URL-based selection for product type filter
  if (filter.name === EFilterKeys.PRODUCT_TYPE && showGroupedPage) {
    const { currentValue: urlBasedValue } = handleUrlBasedProductTypeSelection(
      filter,
      options,
      routerAsPath,
      selectedFiltersState,
      subCollectionCards,
    )
    currentValue = urlBasedValue
  }

  return {
    filterName,
    state,
    currentValue,
    options,
  }
}
